import { useLoyaltyContext } from "../../../services/hooks/loyalty/useLoyaltyContext";
import { useIsLoyaltyOfferInstanceActive } from "../../../services/hooks/loyalty/useLoyaltyOfferInstanceActive";
import { useMenuLookups } from "../../../services/hooks/useMenuLookups";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { removeLoyaltyOffer } from "../../../services/redux/slices/cartLoyaltyV2Slice";
import {
  getMenuSubItemsForOffer,
  useApplyLoyaltyItemOfferFlow,
} from "../../../services/redux/thunks/applyLoyaltyItemOfferFlow";
import { LocalizedString } from "../../../services/redux/types/CartLoyaltyV2Types";
import * as textPreparation from "../../../utils/help/textPreparation/textPreparation";
import LoyaltyOffer from "../../../utils/LoyaltyOfferClass/LoyaltyOffer";
import Offer from "../../../utils/OfferClass/Offer/Offer";
import { LoyaltyOfferIcon } from "../LoyaltyOfferIcon/LoyaltyOfferIcon";
import {
  PromoBannerActionButton,
  PromoBannerCard,
} from "../PromoBanner/PromoBannerCard";

type LoyaltyFlashOfferBannerProps = {
  offer: LoyaltyOffer;
  offersTab?: boolean;
  /** Sizes the copy as the tiers design does, which sets the banner's height. */
  tierSized?: boolean;
  /**
   * The campaign's own words for this offer, when it authored any (story
   * #4087's server-fed rail). Absent everywhere else, which is why the offer's
   * `uiConfig` remains the default: this overrides only the copy, never the
   * offer's state, so apply/remove, expiry and availability still come from the
   * offer itself.
   */
  copy?: { title?: LocalizedString; description?: LocalizedString };
};

export default function LoyaltyFlashOfferBanner({
  offer,
  offersTab,
  tierSized,
  copy,
}: LoyaltyFlashOfferBannerProps) {
  const { t } = useTranslationI18();
  const dispatch = useAppDispatch();
  const { timeZone, mappedOrderType, channel } = useLoyaltyContext();

  // ------- Selectors -------
  const selectedLanguage = useAppSelector(
    (state) => state.config.selectedLanguage
  );
  const isOfferInstanceActive = useIsLoyaltyOfferInstanceActive();
  const { subItemLookup } = useMenuLookups();
  const applyLoyaltyItemOfferFlow = useApplyLoyaltyItemOfferFlow();
  const partnershipCustomer = useAppSelector(
    (state) => state.loyaltyCustomerInfo?.data?.partnership
  );
  const entityImageLookup = useAppSelector(
    (state) => state.config.entityImageLookup
  );
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);

  // ------- Variables -------
  const applicabilityResult = offer.isOfferApplicable({
    timeZone,
    orderType: mappedOrderType ?? undefined,
    channel,
    entityImageLookup,
    itemStatusRedux,
    formatTime: (time) => textPreparation.formatTimeAMPMLocalized(t, time),
  });
  const hasDeletedMenuItems = offer.hasDeletedItems(entityImageLookup);
  const isDisabled = LoyaltyOffer.isLoyaltyOfferBlocked(
    false,
    applicabilityResult.applicable,
    hasDeletedMenuItems
  );
  const unavailableChipText = LoyaltyOffer.getLoyaltyOfferStatusChipText(
    isDisabled,
    applicabilityResult,
    t
  );

  const lang = selectedLanguage as keyof LocalizedString;
  const offerIdentifier = offer.shortId ?? offer.id;

  const isRedeemed = isOfferInstanceActive(offer);
  const description =
    Offer.getLocalizedValue(copy?.description) ?? offer.getDescription(lang);
  const expiryDate = offer.getExpiryDate();
  const isExpiryWithin60Days = offer.isExpiryWithinDays(60);

  const extraPadding = offer.isBogoOffer() || offer.isBundleOffer();

  // ------- Texts -------
  const remove = textPreparation.capitalizeFirstLetter(t("offers:remove"));
  const apply = textPreparation.capitalizeFirstLetter(t("offers:apply"));
  const expDate = t("offers:exp-date");

  // ------- Functions -------
  const createMarkup = (html: string) => ({ __html: html });

  const handleApplyOffer = () => {
    const plain = offer.toPlain();
    applyLoyaltyItemOfferFlow({
      offer: plain,
      menuSubItems: getMenuSubItemsForOffer(plain, subItemLookup),
    });
  };

  const handleRemoveOffer = () => {
    dispatch(removeLoyaltyOffer());
  };

  return (
    <PromoBannerCard
      testId={`loyalty_offer_slider_${offerIdentifier}`}
      organization={Boolean(partnershipCustomer)}
      active={isRedeemed}
      unavailable={isDisabled}
      extraPadding={extraPadding}
      extraMargin={extraPadding && !offersTab}
      tierSized={tierSized}
      icon={
        // Primary, not the tier colour: a flash offer carries the restaurant's
        // identity (main, #227) — the tier tint belongs to tier surfaces.
        <LoyaltyOfferIcon
          offer={offer}
          sliderStyle
          backgroundIconColor="primary"
        />
      }
      title={
        <span
          dangerouslySetInnerHTML={createMarkup(
            Offer.getLocalizedValue(copy?.title) ?? offer.getTitle(lang)
          )}
        />
      }
      description={
        description ? (
          <span dangerouslySetInnerHTML={createMarkup(description)} />
        ) : undefined
      }
      chip={
        unavailableChipText
          ? {
              text: textPreparation.capitalizeFirstLetter(unavailableChipText),
              testId: "loyalty_slider-offer-unavailable",
            }
          : undefined
      }
      expiry={
        expiryDate && isExpiryWithin60Days
          ? {
              label: expDate,
              value: expiryDate,
              testId: `loyalty_${offerIdentifier}-expiryDate`,
            }
          : undefined
      }
      action={
        isRedeemed ? (
          <PromoBannerActionButton
            variant="remove"
            onClick={handleRemoveOffer}
            testId={`loyalty_remove_offer_${offerIdentifier}`}
          >
            {remove}
          </PromoBannerActionButton>
        ) : (
          !isDisabled && (
            <PromoBannerActionButton
              onClick={handleApplyOffer}
              testId={`loyalty_apply_offer_${offerIdentifier}`}
            >
              {apply}
            </PromoBannerActionButton>
          )
        )
      }
    />
  );
}
